import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertCircle, BarChart3, Plus, Video } from "lucide-react";
import type {
  ChannelMetadata,
  SessionRecordingSummary,
  TelemetryDataPoint,
  TelemetryNote,
  VideoFile,
} from "@/types";
import type { VideoSessionTimeline } from "@/utils/videoSessionTimeline";
import type { TimelineRange } from "@/utils/sharedTimeline";
import type { SessionLapMarker, SessionLapTiming } from "@/utils/lapTimeline";
import type {
  ChartControls,
  VideoControls,
} from "@/hooks/useAnalyticsKeyboardShortcuts";
import type { TelemetryTimestampSource } from "@/hooks/useTelemetryTimestamp";
import {
  getChannelColor,
  type ChannelColorIndices,
} from "@/components/telemetry/channelColors";
import { CompactChannelSelector } from "@/components/telemetry/CompactChannelSelector";
import { TelemetryChart } from "@/components/telemetry/TelemetryChart";
import { TelemetryNotesList } from "@/components/telemetry/TelemetryNoteMarkers";
import BottomSheet from "@/components/common/BottomSheet";
import SynchronizedVideoPlayer from "@/components/video/SynchronizedVideoPlayer";
import VideoSessionTimelineLane from "@/components/video/VideoSessionTimelineLane";

interface MobileAnalyticsLayoutProps {
  sessionId: string;
  videos: VideoFile[];
  recordings?: SessionRecordingSummary[];
  currentTime: number;
  onVideoTimeUpdate: (time: number) => void;
  onVideoSelect: (videoId: string) => void;
  initialVideoId?: string;
  /** Deep-linked video id, forwarded to the player; see SynchronizedVideoPlayer */
  requestedVideoId?: string;
  onDurationChange: (videoId: string, duration: number) => void;
  onGetVideoControls: (controls: VideoControls) => void;
  volume: number;
  onVolumeChange: (volume: number) => void;
  onRefreshRecordingPlayback?: (recordingId: string) => Promise<string | null>;
  syncOffset: number;
  onSyncOffsetChange?: (offset: number) => void;
  videoCreatedAt?: string;
  telemetryStartTime: string | null;
  telemetryTimestampSource: TelemetryTimestampSource;
  timeline: VideoSessionTimeline | null;
  timelineViewport: TimelineRange | null;
  laps: SessionLapTiming[];
  onLapClick: (marker: SessionLapMarker) => void;
  onVideoTimelineTimeUpdate: (time: number) => void;
  chartData: TelemetryDataPoint[];
  chartLoading: boolean;
  chartError: Error | null;
  selectedChannels: string[];
  channelColorIndices: ChannelColorIndices;
  channelMetadata: Record<string, ChannelMetadata>;
  syncCurrentTime: number;
  onChartTimeUpdate: (time: number) => void;
  onHoverTimeUpdate: (time: number | null) => void;
  onGetChartControls: (controls: ChartControls) => void;
  autoCenter: boolean;
  onAutoCenterChange: (enabled: boolean) => void;
  showGrid: boolean;
  onShowGridChange: (enabled: boolean) => void;
  chartHeight: number;
  onChartHeightChange: (height: number) => void;
  chartViewport: { startTime: number; endTime: number } | null;
  onChartViewportChange: (
    viewport: { startTime: number; endTime: number } | null,
  ) => void;
  channels: Record<string, ChannelMetadata>;
  onChannelSelectionChange: (channels: string[]) => void;
  notes: TelemetryNote[];
  onNoteClick: (note: TelemetryNote) => void;
  onNoteCreate: (timeOffset: number) => void;
  onNoteUpdate: (noteId: string, newTimeOffset: number) => void;
  selectedNoteId: string | null;
  notesEnabled: boolean;
  showNotesPanel: boolean;
  /**
   * Must be referentially stable (useCallback) — BottomSheet keys its effects
   * on onClose and an unstable callback re-runs them (re-stealing focus) on
   * every parent render.
   */
  onCloseNotesPanel: () => void;
  onNotesTimeSeek: (timeOffset: number) => void;
}

/**
 * Stacked review layout for the session analytics page below `lg`: video on
 * top, video-timeline strip, telemetry chart, channel chip row. The channel
 * picker and notes list render in BottomSheets. Props-only component — all
 * state lives in SessionAnalyticsPage, which wires it.
 */
export default function MobileAnalyticsLayout({
  sessionId,
  videos,
  recordings,
  currentTime,
  onVideoTimeUpdate,
  onVideoSelect,
  initialVideoId,
  requestedVideoId,
  onDurationChange,
  onGetVideoControls,
  volume,
  onVolumeChange,
  onRefreshRecordingPlayback,
  syncOffset,
  onSyncOffsetChange,
  videoCreatedAt,
  telemetryStartTime,
  telemetryTimestampSource,
  timeline,
  timelineViewport,
  laps,
  onLapClick,
  onVideoTimelineTimeUpdate,
  chartData,
  chartLoading,
  chartError,
  selectedChannels,
  channelColorIndices,
  channelMetadata,
  syncCurrentTime,
  onChartTimeUpdate,
  onHoverTimeUpdate,
  onGetChartControls,
  autoCenter,
  onAutoCenterChange,
  showGrid,
  onShowGridChange,
  chartHeight,
  onChartHeightChange,
  chartViewport,
  onChartViewportChange,
  channels,
  onChannelSelectionChange,
  notes,
  onNoteClick,
  onNoteCreate,
  onNoteUpdate,
  selectedNoteId,
  notesEnabled,
  showNotesPanel,
  onCloseNotesPanel,
  onNotesTimeSeek,
}: MobileAnalyticsLayoutProps) {
  const { t } = useTranslation(["pages", "common"]);
  const [channelsSheetOpen, setChannelsSheetOpen] = useState(false);

  const closeChannelsSheet = useCallback(() => {
    setChannelsSheetOpen(false);
  }, []);

  // Opening the notes panel wins over the channels sheet.
  useEffect(() => {
    if (showNotesPanel) {
      setChannelsSheetOpen(false);
    }
  }, [showNotesPanel]);

  return (
    <div className="flex h-full min-h-0 flex-col bg-[#0d0d14]">
      <div className="flex-shrink-0 bg-black">
        {videos.length > 0 ? (
          <SynchronizedVideoPlayer
            key={sessionId}
            videos={videos}
            recordings={recordings}
            currentTime={currentTime}
            onTimeUpdate={onVideoTimeUpdate}
            onVideoSelect={onVideoSelect}
            initialVideoId={initialVideoId}
            requestedVideoId={requestedVideoId}
            onDurationChange={onDurationChange}
            onGetVideoControls={onGetVideoControls}
            volume={volume}
            onVolumeChange={onVolumeChange}
            syncOffset={syncOffset}
            onSyncOffsetChange={onSyncOffsetChange}
            videoCreatedAt={videoCreatedAt}
            telemetryStartTime={telemetryStartTime ?? undefined}
            telemetryTimestampSource={telemetryTimestampSource}
            timeline={timeline}
            onRefreshRecordingPlayback={onRefreshRecordingPlayback}
            className="w-full"
          />
        ) : (
          <div className="flex aspect-video items-center justify-center bg-black text-[#374151]">
            <div className="text-center">
              <Video className="h-10 w-10 mx-auto mb-3 opacity-20" />
              <p className="text-sm font-medium text-[#4b5563]">
                {t("pages:sessions.analytics.noVideoAvailable")}
              </p>
              <p className="text-xs mt-1 text-[#374151]">
                {t("pages:sessions.analytics.uploadVideosHint")}
              </p>
            </div>
          </div>
        )}
      </div>

      {timeline && timeline.clips.length > 0 && (
        <div className="flex-shrink-0">
          <VideoSessionTimelineLane
            timeline={timeline}
            currentTime={currentTime}
            viewport={timelineViewport}
            syncOffset={syncOffset}
            notes={notes}
            onTimeChange={onVideoTimelineTimeUpdate}
            onNoteClick={onNoteClick}
            onNoteCreate={onNoteCreate}
            notesEnabled={notesEnabled}
            laps={laps}
            onLapClick={onLapClick}
          />
        </div>
      )}

      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto bg-[#080810]">
        {chartLoading ? (
          <div className="flex-1 flex items-center justify-center bg-[#080810]">
            <div className="text-center">
              <div className="h-7 w-7 border-2 border-[#3b82f6] border-t-transparent rounded-full mx-auto mb-4 animate-spin" />
              <p className="text-[#4b5563] text-sm">
                {t("pages:sessions.analytics.loadingChartData")}
              </p>
            </div>
          </div>
        ) : chartError ? (
          <div className="flex-1 flex items-center justify-center bg-[#080810]">
            <div className="text-center">
              <AlertCircle className="h-8 w-8 text-[#f87171] mx-auto mb-4 opacity-60" />
              <p className="text-[#f87171] text-sm opacity-80">
                {t("pages:sessions.analytics.failedToLoadChartData")}
              </p>
            </div>
          </div>
        ) : selectedChannels.length === 0 ? (
          <div className="flex-1 flex items-center justify-center bg-[#080810]">
            <div className="text-center">
              <BarChart3 className="h-10 w-10 text-[#374151] mx-auto mb-4 opacity-40" />
              <p className="text-base font-medium text-[#4b5563] mb-2">
                {t("pages:sessions.analytics.selectChannels")}
              </p>
              <p className="text-[#374151] text-sm">
                {t("pages:sessions.analytics.chooseChannelsHint")}
              </p>
            </div>
          </div>
        ) : (
          <TelemetryChart
            data={chartData}
            selectedChannels={selectedChannels}
            channelColorIndices={channelColorIndices}
            channelMetadata={channelMetadata}
            sessionId={sessionId}
            syncCurrentTime={syncCurrentTime}
            onTimeUpdate={onChartTimeUpdate}
            onHoverTimeUpdate={onHoverTimeUpdate}
            onGetChartControls={onGetChartControls}
            autoCenter={autoCenter}
            onAutoCenterChange={onAutoCenterChange}
            showGrid={showGrid}
            onShowGridChange={onShowGridChange}
            chartHeight={chartHeight}
            onChartHeightChange={onChartHeightChange}
            chartViewport={chartViewport}
            onChartViewportChange={onChartViewportChange}
            notes={notes}
            onNoteClick={onNoteClick}
            onNoteCreate={onNoteCreate}
            onNoteUpdate={onNoteUpdate}
            selectedNoteId={selectedNoteId}
            notesEnabled={notesEnabled}
            className="w-full"
          />
        )}
      </div>

      <div
        className="flex flex-shrink-0 items-center gap-2 overflow-x-auto border-t border-[#1a1a2e] bg-[#0d0d14] px-3 py-2"
        style={{ paddingBottom: "calc(0.5rem + env(safe-area-inset-bottom))" }}
      >
        {selectedChannels.map((channel, index) => (
          <span
            key={channel}
            className="inline-flex flex-shrink-0 items-center gap-1.5 rounded-full border border-[#1f2937] bg-[#111827] px-2.5 py-1 text-xs text-[#6b7280]"
          >
            <span
              className="h-2 w-2 rounded-full"
              style={{
                background: getChannelColor(channel, channelColorIndices, index),
              }}
            />
            {channel}
          </span>
        ))}
        <button
          type="button"
          data-testid="open-channels-sheet"
          onClick={() => {
            if (showNotesPanel) onCloseNotesPanel();
            setChannelsSheetOpen(true);
          }}
          aria-label={t("pages:sessions.analytics.openChannelsSheet")}
          className="inline-flex min-h-11 min-w-11 flex-shrink-0 items-center justify-center gap-1 rounded-full border border-dashed border-[#1f2937] px-2.5 text-[#6b7280]"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>

      <BottomSheet
        open={channelsSheetOpen}
        onClose={closeChannelsSheet}
        title={t("pages:sessions.analytics.channelsSheetTitle")}
      >
        <CompactChannelSelector
          channels={channels}
          selectedChannels={selectedChannels}
          channelColorIndices={channelColorIndices}
          onSelectionChange={onChannelSelectionChange}
          maxSelections={8}
          className="w-full"
        />
      </BottomSheet>

      <BottomSheet
        open={showNotesPanel}
        onClose={onCloseNotesPanel}
        title={t("pages:sessions.analytics.telemetryNotes")}
      >
        <TelemetryNotesList
          notes={notes}
          onNoteClick={onNoteClick}
          onTimeSeek={onNotesTimeSeek}
          selectedNoteId={selectedNoteId}
        />
      </BottomSheet>
    </div>
  );
}
